'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useAuiEvent, useAuiState } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { isAwaitingHitlAction } from '@/components/assistant-ui/hitl/use-hitl-call';
import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { buildThreadStreamUrl, getResumableThreadUuid } from '@/lib/hitl/resumable-storage';
import { resolveThreadStatusKey, useThreadStatusStore } from '@/lib/thread-status/store';
import { resolveServerResourceId } from '@/lib/utils/resource-id';
import { findThread, getThreadUuid } from '@/lib/utils/thread';

const RESUMABLE_RECORD_POLL_MS = 2000;
const BACKGROUND_STREAM_POLL_MS = 4000;

/**
 * Feeds the thread-status store from the runtime's seams: run start/end events (any thread,
 * including background ones), the active thread's pending HITL call, thread switches (clears
 * 'unread'), and — for resumable tenants — a stream record left over from a reload, which means
 * a run may still be producing server-side.
 */
export function useThreadStatusFeed(): void {
  const tenantName = useTenantName();
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  // Thread-row status indicators are part of the per-tenant server-persistence feature bundle.
  // Gate ALL store writes on it so non-opted (production) tenants get zero status → zero dots →
  // byte-identical thread list. This is the single opt-in switch to remove at feature GA.
  const indicatorsEnabled = useConfigValue<'local' | 'server'>('persistence') === 'server';

  const threadItems = useAuiState(state => state.threads.threadItems);
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);
  const threadItemsRef = useLatest(threadItems);

  const resolveKey = useCallback(
    (threadId: string) => {
      const thread = findThread(threadItemsRef.current, threadId);
      return thread ? resolveThreadStatusKey(thread) : getThreadUuid(threadId);
    },
    [threadItemsRef],
  );

  const runSeenKeysRef = useRef<Set<string>>(new Set());

  useAuiEvent('thread.runStart', ({ threadId }: { threadId: string }) => {
    if (!indicatorsEnabled) return;
    const key = resolveKey(threadId);
    runSeenKeysRef.current.add(key);
    useThreadStatusStore.getState().markRunStart(key);
  });

  useAuiEvent('thread.runEnd', ({ threadId }: { threadId: string }) => {
    if (!indicatorsEnabled) return;
    const key = resolveKey(threadId);
    runSeenKeysRef.current.add(key);
    useThreadStatusStore.getState().markRunEnd(key);
  });

  useEffect(() => {
    if (!indicatorsEnabled || !mainThreadId) return;
    useThreadStatusStore.getState().setActiveThread(resolveKey(mainThreadId));
  }, [indicatorsEnabled, mainThreadId, resolveKey]);

  // Pending HITL is only observable on the mounted (main) thread; a background thread that was
  // switched away from keeps its 'awaiting-approval' status until the user returns and resolves.
  const awaitingHitl = useAuiState(state =>
    isAwaitingHitlAction(state.threads.main.messages.at(-1)?.status),
  );

  useEffect(() => {
    if (!indicatorsEnabled || !mainThreadId) return;
    useThreadStatusStore
      .getState()
      .setAwaitingApproval({ threadId: resolveKey(mainThreadId), awaiting: awaitingHitl });
  }, [indicatorsEnabled, awaitingHitl, mainThreadId, resolveKey]);

  // Background threads keep 'streaming' forever without this: switching away severs the client
  // stream (no runEnd ever fires), while the server run keeps going. Poll the stream-discovery
  // endpoint for every backgrounded streaming thread — 204 means the run finalized server-side,
  // so the dot flips to 'unread'. Cheap (one HEAD-sized GET per streaming thread per 4s, and
  // there is rarely more than one) — no push channel needed.
  const backgroundStreamingKeys = useThreadStatusStore(state =>
    Object.entries(state.statuses)
      .filter(([key, status]) => status === 'streaming' && key !== state.activeThreadId)
      .map(([key]) => key)
      .join(','),
  );
  const posthog = useSafePostHog();
  const posthogRef = useLatest(posthog);

  useEffect(() => {
    if (!resumableEnabled || !backgroundStreamingKeys) return;
    const keys = backgroundStreamingKeys.split(',').filter(Boolean);
    const checkLiveness = async () => {
      const resourceId = resolveServerResourceId(posthogRef.current?.get_distinct_id());
      for (const key of keys) {
        try {
          const response = await fetch(
            buildThreadStreamUrl({ tenantName, threadUuid: key, resourceId }),
            { credentials: 'include' },
          );
          if (response.status === 204) {
            useThreadStatusStore.getState().markRunEnd(key);
          }
        } catch {
          // Transient network failure — next tick retries.
        }
      }
    };
    const interval = setInterval(() => void checkLiveness(), BACKGROUND_STREAM_POLL_MS);
    return () => clearInterval(interval);
  }, [resumableEnabled, backgroundStreamingKeys, tenantName, posthogRef]);

  // A resumable record surviving a reload means that thread's run may still be streaming
  // server-side. Mark it and poll: a real run event hands ownership to the handlers above; the
  // record disappearing (resume replay finished, or the storage dropped it) clears the marker.
  useEffect(() => {
    if (!resumableEnabled) return;
    const threadUuid = getResumableThreadUuid(tenantName);
    if (!threadUuid) return;
    useThreadStatusStore.getState().markRunStart(threadUuid);
    const interval = setInterval(() => {
      if (runSeenKeysRef.current.has(threadUuid)) {
        clearInterval(interval);
        return;
      }
      if (getResumableThreadUuid(tenantName) !== threadUuid) {
        clearInterval(interval);
        useThreadStatusStore.getState().clearStatus(threadUuid);
      }
    }, RESUMABLE_RECORD_POLL_MS);
    return () => clearInterval(interval);
  }, [resumableEnabled, tenantName]);
}
